import {
  DeviceQuantityPropertyType,
  deviceQuantityValue,
  setDevicePropertyQuantity,
} from '@/features/fhir/helpers/device';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface SharedDevices {
  procedureIds: string[];
  devices: fhir4.Device[];
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Colapsa múltiplos espaços internos em um único espaço e remove espaços nas
 * pontas (trim + lowercase já são responsabilidade do chamador).
 */
export function normalizeSpaces(value: string): string {
  return value.replace(/\s+/g, ' ');
}

const normalizeText = (v: string) => normalizeSpaces(v.trim().toLowerCase());

/**
 * Retorna uma chave de identidade lógica para um Device.
 *
 * Prioridade:
 * 1. Primeiro coding com `code` válido → ["coding", system|"", String(code)]
 * 2. type.text normalizado              → ["text", "", normalizado]
 * 3. deviceName model-name             → ["text", "", normalizado]
 * 4. primeiro deviceName.name          → ["text", "", normalizado]
 * 5. fallback vazio                    → ["text", "", ""]
 */
export function deviceIdentityKey(
  device: fhir4.Device,
): [string, string, string] {
  const codings = device.type?.coding ?? [];

  // 1. coding com code válido
  const validCoding = codings.find(
    ({ code }) => String(code ?? '').trim() !== '',
  );
  if (validCoding) {
    return ['coding', validCoding.system ?? '', String(validCoding.code)];
  }

  // 2. type.text
  const typeText = device.type?.text ?? '';
  if (typeText.trim() !== '') {
    return ['text', '', normalizeText(typeText)];
  }

  // 3. deviceName com type === 'model-name'
  const modelName =
    (device.deviceName ?? []).find(dn => dn.type === 'model-name')?.name ?? '';
  if (modelName.trim() !== '') {
    return ['text', '', normalizeText(modelName)];
  }

  // 4. primeiro deviceName.name
  const firstName = device.deviceName?.[0]?.name ?? '';
  if (firstName.trim() !== '') {
    return ['text', '', normalizeText(firstName)];
  }

  return ['text', '', ''];
}

// ─── Sorting helpers ──────────────────────────────────────────────────────────

const compareStrings = (a: string, b: string) => {
  if (a < b) {
    return -1;
  }
  if (a > b) {
    return 1;
  }
  return 0;
};

const quantityCodes: DeviceQuantityPropertyType[] = [
  'quantity',
  'min-quantity',
  'max-quantity',
];

const mergeDeviceQuantitiesByMax = (
  base: fhir4.Device,
  incoming: fhir4.Device,
): fhir4.Device => {
  return quantityCodes.reduce(
    (mergedDevice, code) => {
      const baseQty = deviceQuantityValue(base, code);
      const incomingQty = deviceQuantityValue(incoming, code);

      if (baseQty === undefined && incomingQty === undefined) {
        return mergedDevice;
      }

      const maxQty = Math.max(
        baseQty ?? Number.NEGATIVE_INFINITY,
        incomingQty ?? Number.NEGATIVE_INFINITY,
      );

      return setDevicePropertyQuantity(mergedDevice, code, maxQty);
    },
    { ...base },
  );
};

// ─── Main ─────────────────────────────────────────────────────────────────────

/**
 * Agrupa Devices compartilhados entre ServiceRequests.
 *
 * Complexidade:
 *   - O(P · D) para construir o mapa de identidade, onde P = nº de
 *     ServiceRequests e D = nº médio de Devices por procedimento.
 *   - O(U · log U) para ordenar os grupos resultantes (U = identidades únicas).
 *
 * A entrada não é mutada — nenhum objeto recebido é modificado.
 */
export function groupSharedDevicesByProcedureIds(
  serviceRequests: fhir4.ServiceRequest[],
): SharedDevices[] {
  type Entry = {
    procedureIds: Set<string>;
    device: fhir4.Device;
    key: [string, string, string];
  };

  // Passo 1: mapear cada identidade de Device → conjunto de procedimentos que o contém
  const identityMap = serviceRequests.reduce<Map<string, Entry>>((acc, sr) => {
    const srId = sr.id?.trim() ?? '';
    if (!srId) {
      return acc;
    }

    (sr.contained ?? [])
      .filter(
        (r): r is fhir4.Device => (r as fhir4.Device).resourceType === 'Device',
      )
      .forEach(device => {
        const key = deviceIdentityKey(device);
        const mapKey = JSON.stringify(key);
        const existing = acc.get(mapKey);

        if (existing) {
          existing.procedureIds.add(srId);
          existing.device = mergeDeviceQuantitiesByMax(existing.device, device);
        } else {
          acc.set(mapKey, { procedureIds: new Set([srId]), device, key });
        }
      });

    return acc;
  }, new Map());

  // Passo 2: agrupar entradas pelo conjunto de procedureIds
  type GroupAcc = { procedureIds: string[]; entries: Entry[] };

  const groupMap = Array.from(identityMap.values()).reduce<
    Map<string, GroupAcc>
  >((acc, entry) => {
    const sortedIds = Array.from(entry.procedureIds).sort();
    const groupKey = JSON.stringify(sortedIds);
    const existing = acc.get(groupKey);

    if (existing) {
      existing.entries.push(entry);
    } else {
      acc.set(groupKey, { procedureIds: sortedIds, entries: [entry] });
    }

    return acc;
  }, new Map());

  // Passo 3: construir resultado com ordenação determinística
  return Array.from(groupMap.values())
    .map(({ procedureIds, entries }) => ({
      procedureIds,
      devices: [...entries]
        .sort((a, b) =>
          compareStrings(JSON.stringify(a.key), JSON.stringify(b.key)),
        )
        .map(e => e.device),
    }))
    .sort((a, b) =>
      compareStrings(
        JSON.stringify(a.procedureIds),
        JSON.stringify(b.procedureIds),
      ),
    );
}

// ─── Quantity sync helpers ────────────────────────────────────────────────────

/**
 * Aplica as quantidades dos Devices de `groupDevices` no ServiceRequest
 * informado e remove qualquer Device contained que não esteja presente em
 * `groupDevices` (identificado por deviceIdentityKey).
 *
 * A entrada não é mutada.
 */
export function applyDevicesToServiceRequest(
  serviceRequest: fhir4.ServiceRequest,
  groupDevices: fhir4.Device[],
): fhir4.ServiceRequest | null {
  type Quantities = {
    quantity?: number;
    'min-quantity'?: number;
    'max-quantity'?: number;
  };

  const hasQuantityChanged = (
    device: fhir4.Device,
    code: DeviceQuantityPropertyType,
    nextValue?: number,
  ) => {
    return deviceQuantityValue(device, code) !== nextValue;
  };

  const quantitiesByIdentityKey = groupDevices.reduce<Map<string, Quantities>>(
    (acc, device) => {
      const identityKey = JSON.stringify(deviceIdentityKey(device));
      acc.set(identityKey, {
        quantity: deviceQuantityValue(device, 'quantity'),
        'min-quantity': deviceQuantityValue(device, 'min-quantity'),
        'max-quantity': deviceQuantityValue(device, 'max-quantity'),
      });
      return acc;
    },
    new Map(),
  );

  const allowedIdentityKeys = new Set(quantitiesByIdentityKey.keys());

  const { contained = [], supportingInfo = [], ...rest } = serviceRequest;

  // Apenas Devices que existam no grupo (por identity key)
  const filteredContained = contained.filter(resource => {
    if ((resource as fhir4.Device).resourceType !== 'Device') {
      return true;
    }
    const device = resource as fhir4.Device;
    const deviceKey = JSON.stringify(deviceIdentityKey(device));
    return allowedIdentityKeys.has(deviceKey);
  });

  let hasChanges = contained.length !== filteredContained.length;

  const updatedContained = filteredContained.map(resource => {
    if ((resource as fhir4.Device).resourceType !== 'Device') {
      return resource;
    }
    const device = resource as fhir4.Device;

    const deviceKey = JSON.stringify(deviceIdentityKey(device));
    const updatedQuantities = quantitiesByIdentityKey.get(deviceKey);

    if (!updatedQuantities) {
      return device;
    }

    let updatedDevice = { ...device };

    ['quantity', 'min-quantity', 'max-quantity'].forEach(code => {
      const qtyCode = code as DeviceQuantityPropertyType;

      const nextValue = updatedQuantities[qtyCode];
      if (nextValue !== undefined) {
        if (hasQuantityChanged(device, qtyCode, nextValue)) {
          hasChanges = true;
        }

        updatedDevice = setDevicePropertyQuantity(
          updatedDevice,
          qtyCode,
          nextValue,
        );
      }
    });

    return updatedDevice;
  });

  const remainingIds = new Set(
    updatedContained
      .filter(r => (r as fhir4.Device).resourceType === 'Device')
      .map(r => (r as fhir4.Device).id)
      .filter(Boolean),
  );

  const updatedSupportingInfo = supportingInfo.filter(({ reference }) => {
    if (!reference?.startsWith('#')) {
      return true;
    }
    return remainingIds.has(reference.slice(1));
  });

  if (updatedSupportingInfo.length !== supportingInfo.length) {
    hasChanges = true;
  }

  if (!hasChanges) {
    return null;
  }

  return {
    ...rest,
    contained: updatedContained,
    supportingInfo: updatedSupportingInfo,
  };
}

/**
 * Aplica as quantidades e remoções de `groups` em cada ServiceRequest da lista.
 * Apenas os procedimentos referenciados em algum grupo são afetados.
 */
export function applyDevicesToServiceRequests(
  serviceRequests: fhir4.ServiceRequest[],
  groups: SharedDevices[],
): fhir4.ServiceRequest[] {
  // Montar mapa: procedureId → devices do grupo que o inclui
  const devicesByProcedureId = serviceRequests.reduce<
    Map<string, fhir4.Device[]>
  >((acc, sr) => {
    const srId = sr.id?.trim() ?? '';
    if (!srId) {
      return acc;
    }

    const srDevices = groups
      .filter(g => g.procedureIds.includes(srId))
      .flatMap(g => g.devices);

    acc.set(srId, srDevices);
    return acc;
  }, new Map());

  return serviceRequests.map(sr => {
    const srId = sr.id?.trim() ?? '';
    const srDevices = srId ? devicesByProcedureId.get(srId) : undefined;

    if (!srDevices) {
      return sr;
    }

    return applyDevicesToServiceRequest(sr, srDevices) ?? sr;
  });
}
